import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { AuthService } from '@services/auth/auth.service';
import { ButtonComponent } from '@shared/components/button/button.component';
import { LucideAngularModule } from 'lucide-angular';

@Component({
	selector: 'app-verify-email',
	imports: [TranslocoModule, ButtonComponent, LucideAngularModule],
	templateUrl: './verify-email.component.html',
	styleUrl: './verify-email.component.scss',
})
export class VerifyEmailComponent implements OnInit, OnDestroy {
	status: 'pending' | 'verifying' | 'success' | 'error' = 'pending';
	email: string = '';
	errorMessage: string = '';
	resendDisabled = false;
	resendCountdown = 0;

	constructor(
		private route: ActivatedRoute,
		private router: Router,
		private authService: AuthService,
		private translocoService: TranslocoService,
	) {}

	ngOnInit(): void {
		// Check query params
		const token =
			this.route.snapshot.queryParamMap.get('token_hash') || this.route.snapshot.queryParamMap.get('token');
		const type = this.route.snapshot.queryParamMap.get('type');
		const pending = this.route.snapshot.queryParamMap.get('pending');
		this.email = this.route.snapshot.queryParamMap.get('email') || '';

		if (token) {
			// User clicked verification link
			this.verifyEmail(token, type || undefined);
		} else if (pending === 'true') {
			// User just registered, show pending state
			this.status = 'pending';
		} else {
			// No token and no pending flag, redirect to login
			this.router.navigate(['/login']);
		}
	}

	private verifyEmail(token: string, type?: string): void {
		this.status = 'verifying';

		this.authService.verifyEmail(token, type).subscribe({
			next: () => {
				this.status = 'success';
			},
			error: (err) => {
				this.status = 'error';
				this.errorMessage = err.message || this.translocoService.translate('auth.verifyEmail.error.message');
			},
		});
	}

	resendEmail(): void {
		if (!this.email || this.resendDisabled) {
			return;
		}

		// Mismo comportamiento en éxito y en error (el cooldown evita el spam),
		// y UN solo temporizador con limpieza: el anterior seguía latiendo sobre
		// el componente destruido si se navegaba a mitad de cuenta atrás.
		this.authService.resendVerificationEmail(this.email).subscribe({
			next: () => this.startResendCooldown(),
			error: () => this.startResendCooldown(),
		});
	}

	private resendTimer: ReturnType<typeof setInterval> | null = null;

	private startResendCooldown(): void {
		this.resendDisabled = true;
		this.resendCountdown = 60;
		if (this.resendTimer) clearInterval(this.resendTimer);
		this.resendTimer = setInterval(() => {
			this.resendCountdown--;
			if (this.resendCountdown <= 0) {
				this.resendDisabled = false;
				if (this.resendTimer) clearInterval(this.resendTimer);
				this.resendTimer = null;
			}
		}, 1000);
	}

	navigateToLogin(): void {
		this.router.navigate(['/login']);
	}

	ngOnDestroy(): void {
		if (this.resendTimer) clearInterval(this.resendTimer);
	}
}
